import { Helmet } from "react-helmet-async";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosService } from "../../services/axios/axiosService";
import CandidateProfileStats from "./CandidateProfileStats";
import CandidateProfileTable from "./CandidateProfileTable";
import AddCandidateDialog from "./AddCandidateDialog";
import CandidateProfileDetailDialog from "./CandidateProfileDetailDialog";
import { APP_TITLE } from "../../constant";

const CandidateProfile = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [dashboardData, setDashboardData] = useState([]);
  const stableRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await AxiosService.get("/api/candidate/dashboard");
      // AxiosService already returns response.data; support flat or nested payload.
      const payload =
        res && typeof res === "object" && !Array.isArray(res) && res.data != null ? res.data : res;
      setDashboardData(payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {});

      const list =
        payload?.candidateDTOs ??
        res?.candidateDTOs ??
        (Array.isArray(payload) ? payload : null) ??
        (Array.isArray(res) ? res : []);

      setRows(Array.isArray(list) ? list : []);
    } catch {
      setRows([]);
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  return (
    <>
      <Helmet>
        <title>Candidate Profile | {APP_TITLE}</title>
      </Helmet>

      <Box
        sx={{
          p: { xs: 2, sm: 3.5 },
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 3, sm: 2 }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 650,
              color: theme.palette.text.primary,
              lineHeight: 1.25,
              pt: { xs: 0.5, sm: 0 },
            }}
          >
            Candidate Management
          </Typography>

          <Button
            variant="contained"
            startIcon={<PersonAddAltOutlinedIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              width: { xs: "100%", sm: "auto" },
              flexShrink: 0,
              py: { xs: 1.25, sm: 1 },
              mt: { xs: 0.5, sm: 0 },
            }}
          >
            Add Candidate
          </Button>
        </Stack>

        <CandidateProfileStats dashboardData={dashboardData} />
        <CandidateProfileTable
          rows={stableRows}
          loading={loading}
          onViewDetails={(row) => {
            setSelectedCandidate(row);
            setDetailOpen(true);
          }}
        />
      </Box>

      <AddCandidateDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={fetchCandidates}
        existingEmails={stableRows.map((r) => r?.candidateEmail ?? r?.email).filter(Boolean)}
      />

      <CandidateProfileDetailDialog
        open={detailOpen}
        candidate={selectedCandidate}
        onClose={() => {
          setDetailOpen(false);
          setSelectedCandidate(null);
        }}
      />
    </>
  );
};

export default CandidateProfile;

